import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'

import { useFlags } from 'launchdarkly-react-client-sdk'
import { toast } from 'react-toastify'
import { useDispatch } from 'react-redux'
import { useForm, FormProvider } from 'react-hook-form'
import { Box, DialogContent } from '@mui/material'

import { get } from 'utils/lodash'
import { handleError } from 'utils/error'
import { useUpdateWorkOrderMutation } from 'api/work-order/updateWorkOrder'
import { CACHE_TAG_WORK_ORDER_DETAILS } from 'api/cacheTagTypes'
import { isWORedesignFeatureAvailable } from 'data/launch-darkly/featureSelectors'
import { getWorkOrderListInvalidatesTags } from 'components/customer-details/content/work-orders/utils'

import T from 'T'
import api from 'api'
import HHBaseDialog from 'components/common/HHBaseDialog'
import HHDialogTitle from 'components/common/HHDialogTitle'
import HHDialogActions from 'components/common/HHDialogActions'
import CancelButton from 'components/buttons/CancelButton'
import SaveButton from 'components/buttons/SaveButton'
import HHFormTextField from 'components/form-fields/v5/HHFormTextField'
import HHConfirmDeleteButton from 'components/common/HHConfirmDeleteButton'
import { noop } from 'lodash'

// eslint-disable-next-line hh/comment-budget -- load-bearing: serviceDate arrives as a Date from the route-manager drawer, which the `= ''` default types away
/** @param {Record<string, any> & { serviceDate?: Date | string, existingPurchaseOrder?: string | number }} props */
const AddEditPurchaseOrderDialog = ({
  isOpen = false,
  workOrderId,
  serviceDate = '',
  configuredServiceId = '',
  existingPurchaseOrder = '',
  onClose = noop,
  onSuccess = noop,
}) => {
  const dispatch = useDispatch()
  const [updateWorkOrder, { isLoading }] = useUpdateWorkOrderMutation()
  const purchaseOrderForm = useForm({ defaultValues: { purchaseOrderNumber: '' } })
  const [isRemoveLoading, setIsRemoveLoading] = useState(false)

  const {
    control,
    reset,
    handleSubmit,
    formState: { errors },
  } = purchaseOrderForm

  const purchaseOrderNumberHelperText = get(errors, 'purchaseOrderNumber.message')
  const flags = useFlags()
  const isWORedesignEnabled = isWORedesignFeatureAvailable({ flags })

  const getSuccessMessage = purchaseOrderNumber => {
    if (!purchaseOrderNumber) {
      return T.REMOVE_PURCHASE_ORDER_SUCCESS_MSG
    }
    return existingPurchaseOrder ? T.PURCHASE_ORDER_UPDATED_SUCCESS_MSG : T.PURCHASE_ORDER_ADDED_SUCCESS_MSG
  }

  const onSubmitHandler = data => {
    const purchaseOrderNumber = get(data, 'purchaseOrderNumber', '')
    if (!purchaseOrderNumber) {
      setIsRemoveLoading(true)
    }

    updateWorkOrder({ id: workOrderId, purchaseOrderNumber })
      .unwrap()
      .then(() => {
        onClose()
        onSuccess()
        toast.success(getSuccessMessage(purchaseOrderNumber))
        const woInvalidateTags = getWorkOrderListInvalidatesTags({ isNew: isWORedesignEnabled, serviceDate, csId: configuredServiceId })
        dispatch(api.util.invalidateTags([CACHE_TAG_WORK_ORDER_DETAILS, ...woInvalidateTags]))
      })
      .catch(handleError)
      .finally(() => {
        setIsRemoveLoading(false)
      })
  }

  const handleKeyDown = event => {
    event.stopPropagation()
    if (event.key === 'Enter' || event.key === 'NumpadEnter') {
      handleSubmit(onSubmitHandler)()
    }
  }

  useEffect(() => {
    if (isOpen) {
      reset({ purchaseOrderNumber: existingPurchaseOrder })
    }
  }, [isOpen])

  return (
    <HHBaseDialog open={isOpen} onClose={onClose} maxWidth="xs" fullWidth>
      <HHDialogTitle title={existingPurchaseOrder ? T.EDIT_PURCHASE_ORDER : T.ADD_PURCHASE_ORDER} onClose={onClose} />
      <DialogContent>
        <FormProvider {...purchaseOrderForm}>
          <Box mt={2}>
            <HHFormTextField
              autoFocus
              error={purchaseOrderNumberHelperText}
              helperText={purchaseOrderNumberHelperText}
              control={control}
              rules={{ required: T.CANNOT_BE_EMPTY }}
              name="purchaseOrderNumber"
              label={`${T.PO} #`}
              placeholder={`${T.PO} #`}
              fullWidth
              deprecatedLabel={false}
              onKeyDown={handleKeyDown}
            />
          </Box>
        </FormProvider>
      </DialogContent>
      <HHDialogActions>
        {existingPurchaseOrder && (
          <HHConfirmDeleteButton
            size="small"
            variant="outlined"
            color="warning"
            loading={isLoading && isRemoveLoading}
            deleteButtonTitle={T.REMOVE}
            confirmTitle={T.REMOVE_PURCHASE_ORDER}
            confirmDescription={T.REMOVE_PURCHASE_ORDER_CONFIRM_MSG}
            confirmButtonTitle={T.REMOVE}
            DeleteButtonProps={{ loading: isLoading && isRemoveLoading, color: 'warning' }}
            onDeleteConfirmed={onSubmitHandler}
          />
        )}
        <Box flex={1} />
        <CancelButton size="medium" onClick={onClose} />
        <SaveButton
          loading={isLoading && !isRemoveLoading}
          label={existingPurchaseOrder ? T.UPDATE : T.SAVE}
          onClick={handleSubmit(onSubmitHandler)}
        />
      </HHDialogActions>
    </HHBaseDialog>
  )
}

AddEditPurchaseOrderDialog.propTypes = {
  isOpen: PropTypes.bool,
  workOrderId: PropTypes.string.isRequired,
  serviceDate: PropTypes.string,
  configuredServiceId: PropTypes.string,
  existingPurchaseOrder: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
}

export default AddEditPurchaseOrderDialog
